<script setup lang="ts">
import { computed, ref } from "vue";
import { withoutKeys } from "@/helpers";
import { Field, type FieldContext } from "vee-validate";
import type { ZodSchema } from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

// Prevent attribute inheritance.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    name: string,
    value?: string,
    rules?: ZodSchema,
  }>(),
  { value: '' }
)

const emit = defineEmits<{ 'update:value': [string] }>()
const showPassword = ref(false)
const typedRules = computed(() => props.rules ? toTypedSchema(props.rules) : undefined)
const $input = ref<HTMLInputElement>()

async function handleInput($e: Event, meta: FieldContext['meta'], handleChange: FieldContext['handleChange'], validate: FieldContext['validate']) {
  if (meta.touched || meta.validated) {
    await validate()
  } else if (meta.dirty) {
    await validate({ mode: 'silent' })
  }

  if (meta.valid && $e.target instanceof HTMLInputElement) {
    emit('update:value', $e.target.value)
  }
}
</script>

<template>
  <Field v-slot="{ field, errorMessage, meta, handleChange, validate }" :name="name" :rules="typedRules" :model-value="value">
    <div class="input-group position-relative" :class="[{ 'has-validation': errorMessage, [$style['required']]: $attrs.required }, $style['input-password'], $attrs.class]">
      <input
        ref="$input"
        v-bind="{ ...withoutKeys($attrs, ['class']), ...field }"
        :type="showPassword ? 'text' : 'password'"
        class="form-control"
        :class="{ 'is-invalid': errorMessage }"
        @input="handleInput($event, meta, handleChange, validate)"
      />
      <div class="invalid-tooltip" v-if="errorMessage">{{ errorMessage }}</div>

      <span class="input-group-text" @click="showPassword = !showPassword">
        <FontAwesomeIcon :icon="showPassword ? faEyeSlash : faEye" style="width: 15px" />
      </span>
    </div>
  </Field>
</template>

<style module lang="scss">
@use '@/assets/styles/mixins-inputs';

@import "@/assets/variables";

.input-password {
  @include mixins-inputs.fix-error-tooltips-for-inputs;

  // Hide password reveal.
  &::-ms-reveal {
    display: none;
  }

  input {
    border-right: none;
  }

  :global(.input-group-text) {
    background: none;
  }

  &:focus-within {
    :global {
      .input-group-text {
        border-color: $input-focus-border-color;
      }
    }
  }

  :global {
    .invalid-tooltip + .input-group-text {
      border-color: var(--bs-form-invalid-border-color);
    }
  }
}

.required::after {
  @include mixins-inputs.required-indicator-styles
}
</style>